// Admin Orders / Invoices Dashboard
import { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { useAuth } from '../../context/AuthContext';
import { Button } from '../../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../../components/ui/card';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Badge } from '../../components/ui/badge';
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from '../../components/ui/table';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '../../components/ui/select';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription,
} from '../../components/ui/dialog';
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from '../../components/ui/alert-dialog';
import { toast } from 'sonner';
import {
  Loader2, Eye, Download, Mail, XCircle, IndianRupee, Receipt,
  TrendingUp, FileSpreadsheet, RotateCcw,
} from 'lucide-react';

import { getApiBaseUrl } from '@/lib/apiBase';
const API_URL = getApiBaseUrl();

const STATUS_COLORS = {
  paid: 'bg-green-500/15 text-green-600 border-green-500/30',
  refunded: 'bg-orange-500/15 text-orange-600 border-orange-500/30',
  cancelled: 'bg-red-500/15 text-red-600 border-red-500/30',
};

const PLAN_OPTIONS = ['monthly', 'quarterly', 'yearly', 'lifetime', 'reaccess'];

const money = (n) => `₹${Number(n || 0).toLocaleString('en-IN', { maximumFractionDigits: 2 })}`;

const downloadBlob = async (url, headers, filename) => {
  const res = await axios.get(url, { headers, responseType: 'blob' });
  const blobUrl = window.URL.createObjectURL(new Blob([res.data]));
  const link = document.createElement('a');
  link.href = blobUrl;
  link.setAttribute('download', filename);
  document.body.appendChild(link);
  link.click();
  link.remove();
  window.URL.revokeObjectURL(blobUrl);
};

export const AdminInvoices = () => {
  const { token } = useAuth();
  const headers = { Authorization: `Bearer ${token}` };

  const [summary, setSummary] = useState(null);
  const [loadingSummary, setLoadingSummary] = useState(true);

  const [filters, setFilters] = useState({ status: 'all', plan: 'all', dateFrom: '', dateTo: '', search: '' });
  const [appliedFilters, setAppliedFilters] = useState(filters);
  const [invoices, setInvoices] = useState([]);
  const [page, setPage] = useState(1);
  const [pages, setPages] = useState(1);
  const [total, setTotal] = useState(0);
  const [loadingList, setLoadingList] = useState(true);

  const [viewInvoice, setViewInvoice] = useState(null);
  const [cancelTarget, setCancelTarget] = useState(null);
  const [busyAction, setBusyAction] = useState(null);
  const [exporting, setExporting] = useState(false);

  const [gstrQuarter, setGstrQuarter] = useState('Q1');
  const [gstrYear, setGstrYear] = useState(new Date().getFullYear());
  const [exportingGstr, setExportingGstr] = useState(false);

  const fetchSummary = useCallback(async () => {
    setLoadingSummary(true);
    try {
      const res = await axios.get(`${API_URL}/admin/invoices/summary`, { headers });
      setSummary(res.data);
    } catch (err) {
      console.error(err);
    } finally {
      setLoadingSummary(false);
    }
  }, [token]);

  const fetchInvoices = useCallback(async () => {
    setLoadingList(true);
    try {
      const params = { page, limit: 20 };
      if (appliedFilters.status !== 'all') params.status = appliedFilters.status;
      if (appliedFilters.plan !== 'all') params.plan = appliedFilters.plan;
      if (appliedFilters.dateFrom) params.date_from = appliedFilters.dateFrom;
      if (appliedFilters.dateTo) params.date_to = appliedFilters.dateTo;
      if (appliedFilters.search) params.search = appliedFilters.search;

      const res = await axios.get(`${API_URL}/admin/invoices`, { headers, params });
      setInvoices(res.data.invoices || []);
      setPages(res.data.pages || 1);
      setTotal(res.data.total || 0);
    } catch (err) {
      console.error(err);
      toast.error('Failed to load invoices');
    } finally {
      setLoadingList(false);
    }
  }, [token, page, appliedFilters]);

  useEffect(() => { fetchSummary(); }, [fetchSummary]);
  useEffect(() => { fetchInvoices(); }, [fetchInvoices]);

  const applyFilters = () => { setPage(1); setAppliedFilters(filters); };
  const resetFilters = () => {
    const cleared = { status: 'all', plan: 'all', dateFrom: '', dateTo: '', search: '' };
    setFilters(cleared);
    setAppliedFilters(cleared);
    setPage(1);
  };

  const handleDownload = async (invoiceNumber) => {
    setBusyAction(`download-${invoiceNumber}`);
    try {
      await downloadBlob(`${API_URL}/admin/invoices/${invoiceNumber}/download`, headers, `Invoice-${invoiceNumber}.pdf`);
    } catch (err) {
      toast.error('Failed to download invoice');
    } finally {
      setBusyAction(null);
    }
  };

  const handleResend = async (invoiceNumber) => {
    setBusyAction(`resend-${invoiceNumber}`);
    try {
      await axios.post(`${API_URL}/invoices/${invoiceNumber}/resend`, {}, { headers });
      toast.success('Invoice email sent');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to resend invoice');
    } finally {
      setBusyAction(null);
    }
  };

  const confirmCancel = async () => {
    if (!cancelTarget) return;
    setBusyAction(`cancel-${cancelTarget}`);
    try {
      await axios.post(`${API_URL}/admin/invoices/${cancelTarget}/cancel`, {}, { headers });
      toast.success('Invoice cancelled');
      setCancelTarget(null);
      fetchInvoices();
      fetchSummary();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to cancel invoice');
    } finally {
      setBusyAction(null);
    }
  };

  const handleExport = async (format) => {
    setExporting(true);
    try {
      const params = { format };
      if (appliedFilters.status !== 'all') params.status = appliedFilters.status;
      if (appliedFilters.plan !== 'all') params.plan = appliedFilters.plan;
      if (appliedFilters.dateFrom) params.date_from = appliedFilters.dateFrom;
      if (appliedFilters.dateTo) params.date_to = appliedFilters.dateTo;
      if (appliedFilters.search) params.search = appliedFilters.search;
      const query = new URLSearchParams(params).toString();
      await downloadBlob(`${API_URL}/admin/invoices/export?${query}`, headers, `invoices.${format === 'pdf' ? 'pdf' : 'csv'}`);
    } catch (err) {
      toast.error('Export failed');
    } finally {
      setExporting(false);
    }
  };

  const handleGstrExport = async (format) => {
    setExportingGstr(true);
    try {
      const query = new URLSearchParams({ quarter: gstrQuarter, year: gstrYear, format }).toString();
      await downloadBlob(`${API_URL}/admin/invoices/export/gstr1?${query}`, headers, `gstr1-${gstrQuarter}-${gstrYear}.${format === 'pdf' ? 'pdf' : 'csv'}`);
    } catch (err) {
      toast.error('GSTR-1 export failed');
    } finally {
      setExportingGstr(false);
    }
  };

  const cards = summary ? [
    { label: 'Total Revenue', value: money(summary.total_revenue), icon: IndianRupee, color: 'text-green-500', bg: 'bg-green-500/10' },
    { label: 'This Month Revenue', value: money(summary.this_month_revenue), icon: TrendingUp, color: 'text-blue-500', bg: 'bg-blue-500/10' },
    { label: 'Total Invoices', value: summary.total_invoices, icon: Receipt, color: 'text-purple-500', bg: 'bg-purple-500/10' },
    { label: `GST Collected (${summary.financial_year})`, value: money(summary.gst_collected_this_fy), icon: FileSpreadsheet, color: 'text-amber-500', bg: 'bg-amber-500/10' },
    { label: 'CGST Collected', value: money(summary.cgst_collected), icon: FileSpreadsheet, color: 'text-amber-500', bg: 'bg-amber-500/10' },
    { label: 'SGST Collected', value: money(summary.sgst_collected), icon: FileSpreadsheet, color: 'text-amber-500', bg: 'bg-amber-500/10' },
    { label: 'IGST Collected', value: money(summary.igst_collected), icon: FileSpreadsheet, color: 'text-amber-500', bg: 'bg-amber-500/10' },
    { label: 'Average Order Value', value: money(summary.average_order_value), icon: IndianRupee, color: 'text-teal-500', bg: 'bg-teal-500/10' },
    { label: 'Refunds This Month', value: money(summary.refunds_this_month), icon: RotateCcw, color: 'text-red-500', bg: 'bg-red-500/10' },
  ] : [];

  return (
    <div data-testid="admin-invoices">
      <div className="mb-8">
        <h1 className="text-3xl font-serif font-medium">Orders & Invoices</h1>
        <p className="text-muted-foreground">GST invoices, revenue summary, and filing exports</p>
      </div>

      {loadingSummary ? (
        <div className="grid grid-cols-1 md:grid-cols-3 lg:grid-cols-4 gap-4 mb-8">
          {[...Array(8)].map((_, i) => <div key={i} className="h-24 bg-muted rounded animate-pulse" />)}
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-3 lg:grid-cols-4 gap-4 mb-8">
          {cards.map((c, i) => (
            <Card key={i}>
              <CardContent className="p-4">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-xs text-muted-foreground">{c.label}</p>
                    <p className="text-xl font-bold mt-1">{c.value}</p>
                  </div>
                  <div className={`w-9 h-9 rounded-lg flex items-center justify-center ${c.bg}`}>
                    <c.icon className={`w-4 h-4 ${c.color}`} />
                  </div>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      <Card className="mb-6">
        <CardHeader><CardTitle className="text-base">Filters</CardTitle></CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 md:grid-cols-5 gap-3 items-end">
            <div>
              <Label className="text-xs">From</Label>
              <Input type="date" value={filters.dateFrom} onChange={(e) => setFilters({ ...filters, dateFrom: e.target.value })} />
            </div>
            <div>
              <Label className="text-xs">To</Label>
              <Input type="date" value={filters.dateTo} onChange={(e) => setFilters({ ...filters, dateTo: e.target.value })} />
            </div>
            <div>
              <Label className="text-xs">Plan</Label>
              <Select value={filters.plan} onValueChange={(v) => setFilters({ ...filters, plan: v })}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Plans</SelectItem>
                  {PLAN_OPTIONS.map((p) => <SelectItem key={p} value={p}>{p}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-xs">Status</Label>
              <Select value={filters.status} onValueChange={(v) => setFilters({ ...filters, status: v })}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Statuses</SelectItem>
                  <SelectItem value="paid">Paid</SelectItem>
                  <SelectItem value="refunded">Refunded</SelectItem>
                  <SelectItem value="cancelled">Cancelled</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-xs">Search</Label>
              <Input placeholder="Invoice no / email / name" value={filters.search} onChange={(e) => setFilters({ ...filters, search: e.target.value })} />
            </div>
          </div>
          <div className="flex gap-2 mt-4">
            <Button onClick={applyFilters}>Apply Filters</Button>
            <Button variant="outline" onClick={resetFilters}>Reset</Button>
          </div>
        </CardContent>
      </Card>

      <Card className="mb-6">
        <CardHeader>
          <div className="flex items-center justify-between">
            <CardTitle className="text-base">Invoices ({total})</CardTitle>
          </div>
        </CardHeader>
        <CardContent>
          {loadingList ? (
            <div className="flex items-center justify-center h-40">
              <Loader2 className="w-6 h-6 animate-spin text-muted-foreground" />
            </div>
          ) : invoices.length === 0 ? (
            <p className="text-sm text-muted-foreground py-8 text-center">No invoices found</p>
          ) : (
            <div className="overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Invoice No</TableHead>
                    <TableHead>Date</TableHead>
                    <TableHead>Customer</TableHead>
                    <TableHead>Plan</TableHead>
                    <TableHead className="text-right">Base</TableHead>
                    <TableHead className="text-right">GST</TableHead>
                    <TableHead className="text-right">Total</TableHead>
                    <TableHead>Method</TableHead>
                    <TableHead>Status</TableHead>
                    <TableHead className="text-right">Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {invoices.map((inv) => {
                    const gst = (inv.cgst_amount || 0) + (inv.sgst_amount || 0) + (inv.igst_amount || 0);
                    return (
                      <TableRow key={inv.invoice_number}>
                        <TableCell className="font-medium">{inv.invoice_number}</TableCell>
                        <TableCell>{inv.invoice_date ? new Date(inv.invoice_date).toLocaleDateString() : '—'}</TableCell>
                        <TableCell>
                          <div>{inv.customer_name}</div>
                          <div className="text-xs text-muted-foreground">{inv.customer_email}</div>
                        </TableCell>
                        <TableCell className="capitalize">{inv.plan_type}</TableCell>
                        <TableCell className="text-right">{money(inv.base_amount)}</TableCell>
                        <TableCell className="text-right">{money(gst)}</TableCell>
                        <TableCell className="text-right font-medium">{money(inv.total_amount)}</TableCell>
                        <TableCell className="capitalize">{inv.payment_method}</TableCell>
                        <TableCell>
                          <Badge variant="outline" className={`capitalize ${STATUS_COLORS[inv.status] || ''}`}>
                            {inv.status}
                          </Badge>
                        </TableCell>
                        <TableCell>
                          <div className="flex justify-end gap-1">
                            <Button size="icon" variant="ghost" onClick={() => setViewInvoice(inv)} title="View">
                              <Eye className="w-4 h-4" />
                            </Button>
                            <Button size="icon" variant="ghost" onClick={() => handleDownload(inv.invoice_number)}
                              disabled={busyAction === `download-${inv.invoice_number}`} title="Download PDF">
                              {busyAction === `download-${inv.invoice_number}`
                                ? <Loader2 className="w-4 h-4 animate-spin" /> : <Download className="w-4 h-4" />}
                            </Button>
                            <Button size="icon" variant="ghost" onClick={() => handleResend(inv.invoice_number)}
                              disabled={busyAction === `resend-${inv.invoice_number}`} title="Resend Email">
                              {busyAction === `resend-${inv.invoice_number}`
                                ? <Loader2 className="w-4 h-4 animate-spin" /> : <Mail className="w-4 h-4" />}
                            </Button>
                            <Button size="icon" variant="ghost" onClick={() => setCancelTarget(inv.invoice_number)}
                              disabled={inv.status === 'cancelled'} title="Cancel">
                              <XCircle className="w-4 h-4 text-red-500" />
                            </Button>
                          </div>
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>

              <div className="flex items-center justify-between mt-4">
                <p className="text-sm text-muted-foreground">Page {page} of {pages} ({total} total)</p>
                <div className="flex gap-2">
                  <Button size="sm" variant="outline" disabled={page <= 1} onClick={() => setPage((p) => p - 1)}>Previous</Button>
                  <Button size="sm" variant="outline" disabled={page >= pages} onClick={() => setPage((p) => p + 1)}>Next</Button>
                </div>
              </div>
            </div>
          )}
        </CardContent>
      </Card>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <Card>
          <CardHeader>
            <CardTitle className="text-base">Bulk Export — Current Filtered Set</CardTitle>
            <CardDescription>Exports respect the filters applied above</CardDescription>
          </CardHeader>
          <CardContent className="flex gap-3">
            <Button variant="outline" onClick={() => handleExport('csv')} disabled={exporting}>
              {exporting ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <FileSpreadsheet className="w-4 h-4 mr-2" />}
              Export CSV
            </Button>
            <Button variant="outline" onClick={() => handleExport('pdf')} disabled={exporting}>
              {exporting ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Download className="w-4 h-4 mr-2" />}
              Export PDF
            </Button>
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle className="text-base">GSTR-1 Quarterly Export</CardTitle>
            <CardDescription>B2C/B2B format for GST filing</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="flex gap-3 items-end mb-3">
              <div>
                <Label className="text-xs">Quarter</Label>
                <Select value={gstrQuarter} onValueChange={setGstrQuarter}>
                  <SelectTrigger className="w-28"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="Q1">Q1 (Apr-Jun)</SelectItem>
                    <SelectItem value="Q2">Q2 (Jul-Sep)</SelectItem>
                    <SelectItem value="Q3">Q3 (Oct-Dec)</SelectItem>
                    <SelectItem value="Q4">Q4 (Jan-Mar)</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label className="text-xs">Year</Label>
                <Input type="number" className="w-24" value={gstrYear} onChange={(e) => setGstrYear(parseInt(e.target.value || '2026', 10))} />
              </div>
            </div>
            <div className="flex gap-3">
              <Button variant="outline" onClick={() => handleGstrExport('csv')} disabled={exportingGstr}>
                {exportingGstr ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <FileSpreadsheet className="w-4 h-4 mr-2" />}
                Export CSV
              </Button>
              <Button variant="outline" onClick={() => handleGstrExport('pdf')} disabled={exportingGstr}>
                {exportingGstr ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Download className="w-4 h-4 mr-2" />}
                Export PDF
              </Button>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* View invoice modal */}
      <Dialog open={!!viewInvoice} onOpenChange={(o) => !o && setViewInvoice(null)}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>Invoice {viewInvoice?.invoice_number}</DialogTitle>
            <DialogDescription>{viewInvoice?.plan_name}</DialogDescription>
          </DialogHeader>
          {viewInvoice && (
            <div className="space-y-2 text-sm">
              <div className="flex justify-between"><span className="text-muted-foreground">Customer</span><span>{viewInvoice.customer_name}</span></div>
              <div className="flex justify-between"><span className="text-muted-foreground">Email</span><span>{viewInvoice.customer_email}</span></div>
              <div className="flex justify-between"><span className="text-muted-foreground">Date</span><span>{new Date(viewInvoice.invoice_date).toLocaleString()}</span></div>
              <div className="flex justify-between"><span className="text-muted-foreground">Base Amount</span><span>{money(viewInvoice.base_amount)}</span></div>
              <div className="flex justify-between"><span className="text-muted-foreground">GST Type</span><span>{viewInvoice.gst_type}</span></div>
              {viewInvoice.gst_type === 'IGST' ? (
                <div className="flex justify-between"><span className="text-muted-foreground">IGST</span><span>{money(viewInvoice.igst_amount)}</span></div>
              ) : (
                <>
                  <div className="flex justify-between"><span className="text-muted-foreground">CGST</span><span>{money(viewInvoice.cgst_amount)}</span></div>
                  <div className="flex justify-between"><span className="text-muted-foreground">SGST</span><span>{money(viewInvoice.sgst_amount)}</span></div>
                </>
              )}
              <div className="flex justify-between font-semibold border-t pt-2"><span>Total</span><span>{money(viewInvoice.total_amount)}</span></div>
              <div className="flex justify-between"><span className="text-muted-foreground">Payment ID</span><span className="truncate max-w-[220px]">{viewInvoice.payment_id}</span></div>
              <div className="flex justify-between"><span className="text-muted-foreground">Status</span>
                <Badge variant="outline" className={`capitalize ${STATUS_COLORS[viewInvoice.status] || ''}`}>{viewInvoice.status}</Badge>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Cancel confirm */}
      <AlertDialog open={!!cancelTarget} onOpenChange={(o) => !o && setCancelTarget(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Cancel invoice {cancelTarget}?</AlertDialogTitle>
            <AlertDialogDescription>
              This marks the invoice as cancelled. It does not process a refund.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Back</AlertDialogCancel>
            <AlertDialogAction onClick={confirmCancel}>Confirm Cancel</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};

export default AdminInvoices;
